import { useSeoMeta } from "@unhead/vue";
import { createRouter, createWebHistory } from "vue-router";
import { defaultSeoMeta } from "./data";
import { USER_ROLES } from "./lib/enums";
import { useUsersStore } from "./stores/users";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "Home",
      component: () => import("./views/HomeView.vue"),
      meta: {},
    },
    {
      path: "/events/:eventId",
      name: "BoothsRequest",
      component: () => import("./views/BoothsRequestView.vue"),
      meta: {
        title: "Booths Request",
        requiredRole: USER_ROLES.SPONSORS,
      },
    },
    {
      path: "/events/:eventId/edit",
      name: "BoothsEdit",
      component: () => import("./views/BoothsEditView.vue"),
      meta: {
        title: "Edit Booths",
        requiredRole: USER_ROLES.EDITORS,
      },
    },
    {
      path: "/admin/events",
      name: "EventsList",
      component: () => import("./views/EventsListView.vue"),
      meta: {
        title: "Events",
        requiredRole: USER_ROLES.EDITORS,
      },
    },
    {
      path: "/admin/events/:eventId",
      name: "EventEdit",
      component: () => import("./views/EventEditView.vue"),
      meta: {
        title: "Edit Event",
        requiredRole: USER_ROLES.EDITORS,
      },
    },
    {
      path: "/admin/events/new",
      name: "EventAdd",
      component: () => import("./views/EventAddView.vue"),
      meta: {
        title: "New Event",
        requiredRole: USER_ROLES.ADMINS,
      },
    },
    {
      path: "/admin/my-account",
      name: "MyAccount",
      component: () => import("./views/user/MyAccountView.vue"),
      meta: {
        title: "My Account",
        requiredRole: USER_ROLES.EDITORS,
      },
    },
    {
      path: "/admin/users",
      name: "UsersList",
      component: () => import("./views/user/UsersListView.vue"),
      meta: {
        title: "Users",
        requiredRole: USER_ROLES.ADMINS,
      },
    },
    {
      path: "/admin/users/:userId",
      name: "UserEdit",
      component: () => import("./views/user/UserEditView.vue"),
      meta: {
        title: "Edit User",
        requiredRole: USER_ROLES.ADMINS,
      },
    },
    {
      path: "/admin/users/add",
      name: "UserAdd",
      component: () => import("./views/user/UserAddView.vue"),
      meta: {
        title: "Add User",
        requiredRole: USER_ROLES.ADMINS,
      },
    },
    {
      path: "/admin/login",
      name: "Login",
      component: () => import("./views/user/LoginView.vue"),
      meta: {
        title: "Login",
        redirectUsers: "MyAccount",
      },
    },
    {
      path: "/admin/logout",
      name: "Logout",
      beforeEnter: async (to, from, next) => {
        const usersStore = useUsersStore();
        await usersStore.logout();
        next({ name: "Login" }); // Redirect to login after logout
      },
      meta: {
        title: "Logout",
      },
    },
    {
      path: "/admin/forgot-password",
      name: "ForgotPassword",
      component: () => import("./views/user/ForgotPasswordView.vue"),
      meta: {
        title: "Forgot Password",
        redirectUsers: "MyAccount",
      },
    },
    {
      path: "/admin/reset-password",
      name: "ResetPassword",
      component: () => import("./views/user/ResetPasswordView.vue"),
      meta: {
        title: "Reset Password",
        redirectUsers: "MyAccount",
      },
    },
    {
      path: "/:path(.*)*",
      name: "NotFound",
      component: () => import("./views/NotFoundView.vue"),
      meta: {
        title: "404",
      },
    },
  ],
});
router.beforeEach(async (to, from, next) => {
  const usersStore = useUsersStore();

  // Fetch session info to determine user role and auth status
  await usersStore.getSessionInfo();

  const isAuthenticated = usersStore.user.isLoggedIn;
  const userRoles = usersStore.roles;

  // Redirect authenticated users away from login and other public pages
  if (isAuthenticated && to.meta.redirectUsers) {
    return next({ name: to.meta.redirectUsers });
  }

  // If the route requires a role
  if (to.meta.requiredRole) {
    // Handle sponsor-specific logic
    if (to.meta.requiredRole === USER_ROLES.SPONSORS) {
      const { token, level } = to.query;
      if (token) {
        await usersStore.getTokenInfo(to.params.eventId, token);

        if (!usersStore.is(USER_ROLES.SPONSORS)) {
          return next({ name: "Home" }); // Or show an error
        }
      } else {
        await usersStore.handleVisitor({
          eventId: to.params.eventId,
          level: level ? decodeURI(level) : null,
        });
      }
      return next();
    }

    // Allow access to admins on all routes
    if (usersStore.is(USER_ROLES.ADMINS)) {
      return next();
    }

    // Allow access based on role hierarchy
    if (
      (to.meta.requiredRole === USER_ROLES.EDITORS &&
        userRoles.includes(USER_ROLES.EDITORS)) ||
      (to.meta.requiredRole === USER_ROLES.SPONSORS &&
        (userRoles.includes(USER_ROLES.EDITORS) ||
          userRoles.includes(USER_ROLES.SPONSORS))) ||
      (to.meta.requiredRole === USER_ROLES.VISITORS &&
        (userRoles.includes(USER_ROLES.EDITORS) ||
          userRoles.includes(USER_ROLES.SPONSORS) ||
          userRoles.includes(USER_ROLES.VISITORS)))
    ) {
      return next();
    }

    if (!isAuthenticated) {
      // Redirect unauthenticated users to login
      return next({ name: "Login" });
    }
    // Redirect unauthorized users to a 404 page or a custom unauthorized page
    return next({ name: "NotFound" });
  }

  next();
});

router.afterEach((to) => {
  useSeoMeta({
    title: to.meta.title || null,
    description: defaultSeoMeta.description,
    titleTemplate: (title) =>
      title ? `${title} | ${defaultSeoMeta.siteName}` : defaultSeoMeta.siteName,
  });
});

export default router;
